import { apiFetch } from '@/shared/api/client';
import type { ComprasFields } from '@/shared/api/controllers/compras';
import type { PagosComprasFields } from '@/shared/api/controllers/pagosCompras';
import type { RetencionPagoFields } from '@/shared/api/controllers/retencionesPago';

/**
 * Backend API wrappers for the Compras module. Stage 1 surfaces the
 * list + eliminar only. Registrar / Editar / Pagar ship in later stages.
 */

/** Importación de la compra, resuelta por el server (la API de Importaciones es sólo admin). */
export type CompraImportacion = { id: number; numero: number; descripcion: string | null };

/**
 * Row returned by `GET /api/compras`. Carries the raw compra `fields`
 * plus two server-aggregated numbers we can't efficiently compute in
 * the browser (would require fetching every pago + retención row).
 */
export type CompraListItem = {
  id: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  fields: ComprasFields;
  /** Σ `Importe_PC` (Status_PC ≠ Anulado) for this compra. */
  importePagado: number;
  /** Σ `ImporteRetenido_RP` (Status_RP = Activo), en la moneda de la compra. */
  importeRetenciones: number;
  /** Subconjunto sin imputar a un pago — el único que descuenta saldo. */
  importeRetencionesPendientes: number;
  /** Σ `Total_CO` of pending NCs (Status_CO ∉ {Anulado, Pagado}) that link to this compra. */
  importeNotasCreditoPendientes: number;
  importacion: CompraImportacion | null;
};

export async function listCompras(): Promise<CompraListItem[]> {
  const res = await apiFetch<{ items: CompraListItem[] }>('/api/compras');
  return res.items;
}

export type EliminarCompraResponse =
  | {
      outcome: 'anulated';
      id: string;
      pagosAnulados: number;
      instrumentosAnulados: number;
      retencionesAnuladas: number;
    }
  | { outcome: 'blocked_status'; message: string };

export async function eliminarCompra(id: string | number): Promise<EliminarCompraResponse> {
  return apiFetch<EliminarCompraResponse>(`/api/compras/${id}/eliminar`, {
    method: 'POST',
  });
}

// ---------------------------------------------------------------------------
// Create (Stage 2)
// ---------------------------------------------------------------------------

/**
 * Nueva Compra line payload — fields mirror the PowerApps cascade
 * (Categoria → CuentaContable → Articulo) mapped onto the
 * `06.ProductoServicioCompras` schema:
 *
 *   - `productoServicio` → `ProductoServicio_PSC` — the Categoria label
 *     (`Categoria_ART` value picked at step 1 of the cascade).
 *   - `centroCosto` → `CentroCosto_PSC` — the Cuenta Contable
 *     (`CuentaContable_ART` value picked at step 2).
 *   - `descripcion` → `Descripcion_PSC` — the Articulo label
 *     (`Articulo_ART` picked at step 3).
 *   - `articuloId` — the SP id of the picked article, kept for future
 *     enhancements (e.g., linking back into the catalog).
 *
 * `ivaRate` is a percentage number — AR uses 21 / 10.5 / 0, MX uses
 * 16 / 8 / 0. The server writes `PorcentajeIVA_PSC` as the formatted
 * string ('10,5%' / '21%' / '16%' / '8%' / 'IVA Exento') and `IVA_PSC`
 * as the computed amount.
 */
export type NuevaCompraLinePayload = {
  articuloId?: string;
  productoServicio: string;
  centroCosto: string;
  descripcion: string;
  observaciones?: string;
  cantidad: number;
  precio: number;
  /** IVA rate as a percentage (e.g. 21, 10.5, 16, 8, 0). 0 = Exento. */
  ivaRate: number;
  /** MX SAT classification — same 16% rate but different CFDI 4.0 +
   *  IVA Compras book grouping. Defaults to 'general' for AR + general
   *  MX 16% lines; 'importacion' for MX import-IVA lines paid at
   *  customs. */
  ivaCategoria?: 'general' | 'importacion';
  // Client-computed; server re-derives.
  subtotal: number;
  iva: number;
  total: number;
};

export type NuevaCompraPercepcionPayload = {
  percepcionImpuesto: string;
  importe: number;
  observaciones?: string;
};

/**
 * Pre-loaded retención at compra creation time. PowerApps wrote these as
 * `RetencionesPago` rows with `IDPago_RP='Pendiente'`; when the first
 * Pagar Factura runs they appear pre-populated, and that pago's confirm
 * step flips them to the pago id so they stop showing as pending.
 */
export type NuevaCompraRetencionPayload = {
  tipo: string;
  conceptoConcat: string;
  descripcion?: string;
  nroComprobante: string;
  fechaComprobante: string; // dd/mm/yyyy
  importeISAR: number;
  importeRetenido: number;
};

export type NuevaCompraPayload = {
  header: {
    /** Importación abierta a la que se asocia. En edición, null la quita y
     *  ausente la deja como está. */
    importacionId?: number | null;
    proveedor: string;
    comprobante: string;
    numeroComprobante: string;
    /** `dd/mm/yyyy` — already in AR format when sent. */
    fechaComprobante: string;
    /**
     * Optional AR fiscal date — `dd/mm/yyyy`. Mirrors PowerApps's
     * `FechaFiscal_CO`; useful when an invoice is registered for IVA
     * purposes in a different period than its issue date. Blank-safe.
     */
    fechaFiscal?: string;
    vencimientoPago: string;
    moneda: 'Pesos Argentinos' | 'Pesos Mexicanos' | 'Dolares';
    tipoCambio?: number;
    observaciones?: string;
    debitoAutomatico: boolean;
    llevaFactura: boolean;
  };
  lines: NuevaCompraLinePayload[];
  percepciones: NuevaCompraPercepcionPayload[];
  /** Pre-loaded retenciones — only sent when llevaFactura=true and the
   *  comprobante type isn't in {NC, Factura C, Otros Comprobantes}. */
  retenciones?: NuevaCompraRetencionPayload[];
  totales: {
    totalBruto: number;
    totalIVA: number;
    totalImpuestos: number;
    totalSinIVA: number;
    total: number;
  };
  /** When set, this compra is a Nota de Crédito applied to the parent compra id. */
  notaCreditoID?: string;
};

export type CreateCompraResponse =
  | {
      id: string;
      numeroComprobante: string;
      total: number;
      linesCreadas: number;
      percepcionesCreadas: number;
      notaCreditoID: string | null;
      fullOffset?: boolean;
    }
  | { outcome: 'invalid_parent'; message: string }
  | { outcome: 'nc_exceeds_balance'; restante: number; message: string }
  | { outcome: 'importacion_no_abierta'; message: string };

export async function createCompra(payload: NuevaCompraPayload): Promise<CreateCompraResponse> {
  return apiFetch<CreateCompraResponse>('/api/compras', {
    method: 'POST',
    body: payload,
  });
}

// ---------------------------------------------------------------------------
// Edit (Stage 4a)
// ---------------------------------------------------------------------------

/**
 * Editar Compra line payload. The per-line `id` is the SharePoint item
 * id of an existing `06.ProductoServicioCompras` row — when present the
 * server patches it; when absent the server creates a new row. Existing
 * rows not mentioned in the payload are soft-deleted (`Status_PSC='Anulado'`).
 */
export type EditarCompraLinePayload = NuevaCompraLinePayload & { id?: string };

export type EditarCompraPercepcionPayload = NuevaCompraPercepcionPayload & { id?: string };

export type EditarCompraPayload = {
  header: NuevaCompraPayload['header'];
  lines: EditarCompraLinePayload[];
  percepciones: EditarCompraPercepcionPayload[];
};

export type EditarCompraResponse =
  | {
      outcome: 'edited';
      id: string;
      lines: { updated: number; created: number; anulated: number };
      percepciones: { updated: number; created: number; anulated: number };
      total: number;
    }
  | { outcome: 'blocked_status'; message: string }
  | { outcome: 'importacion_no_abierta'; message: string };

export async function editarCompra(
  id: string | number,
  payload: EditarCompraPayload,
): Promise<EditarCompraResponse> {
  return apiFetch<EditarCompraResponse>(`/api/compras/${id}`, {
    method: 'PUT',
    body: payload,
  });
}

// --------------------------------------------------------------------------
// Stage 3 — Ver detail + Pagar Factura
// --------------------------------------------------------------------------

export type ProductoServicioCompraFields = {
  IDCompra_PSC?: string | null;
  ProductoServicio_PSC?: string | null;
  CentroCosto_PSC?: string | null;
  Descripcion_PSC?: string | null;
  Observaciones_PSC?: string | null;
  Cantidad_PSC?: string | null;
  Precio_PSC?: string | null;
  IVA_PSC?: string | null;
  PorcentajeIVA_PSC?: string | null;
  /** MX SAT IVA classification. 'general' = domestic 16% / 8% etc.;
   *  'importacion' = 16% paid at customs (CFDI 4.0 + IVA Compras book
   *  split). Always present from the server; older rows default to
   *  'general'. */
  IvaCategoria_PSC?: 'general' | 'importacion' | null;
  MontoTotal_PSC?: string | null;
  Status_PSC?: string | null;
};

export type PercepcionImpuestoCompraFields = {
  IDCompra_PIC?: string | null;
  PercepcionImpuesto_PIC?: string | null;
  Importe_PIC?: string | null;
  Observaciones_PIC?: string | null;
  Status_PIC?: string | null;
};

export type InstrumentoPagoFields = {
  IDCompra_IP?: string | null;
  IDPago_IP?: string | null;
  TipoCuenta_IP?: string | null;
  Cuenta_IP?: string | null;
  Importe_IP?: string | null;
  ImporteAlt_IP?: string | null;
  TipoCambio_IP?: string | null;
  Banco_IP?: string | null;
  Descripcion_IP?: string | null;
  Status_IP?: string | null;
  /** Stage 4b-3 — DriveItem id of the per-instrumento bank receipt. */
  ComprobanteDriveItemID_IP?: string | null;
  ComprobanteFileName_IP?: string | null;
};

export type CompraRecepcionSummary = {
  id: string;
  fecha: string;
  status: string;
};

export type CompraDetailResponse = {
  header: { id: string; fields: ComprasFields };
  importacion?: CompraImportacion | null;
  lines: Array<{ id: string; fields: ProductoServicioCompraFields }>;
  percepciones: Array<{ id: string; fields: PercepcionImpuestoCompraFields }>;
  pagos: Array<{ id: string; fields: PagosComprasFields }>;
  instrumentos: Array<{ id: string; fields: InstrumentoPagoFields }>;
  retenciones: Array<{ id: string; fields: RetencionPagoFields }>;
  /** Pending Notas de Crédito linked to this compra as parent. */
  notasCreditoPendientes: Array<{ id: string; fields: ComprasFields }>;
  /** Recepciones generated against this compra (auto or manual). */
  recepciones: CompraRecepcionSummary[];
};

export async function getCompraDetalle(id: string | number): Promise<CompraDetailResponse> {
  return apiFetch<CompraDetailResponse>(`/api/compras/${id}`);
}
